/* OS EDD - Top Shop (scoped, no globals) */
(() => {
  const SELECTOR_ROOT = '[data-os-edd-top-shop]';
  const SELECTOR_CTA = '[data-osedd-cta]';
  const SELECTOR_STICKY = '[data-osedd-sticky]';
  const SELECTOR_STICKY_CTA = '[data-osedd-sticky-cta]';

  const BOGO_RELOAD_MARKER = '_osedd_bogo_reload';

  function normalizeBogoParamString(rawValue) {
    if (!rawValue) return '';

    let normalized = String(rawValue).trim();
    if (!normalized) return '';

    if (normalized.includes('://')) {
      try {
        normalized = new URL(normalized).search.replace(/^\?/, '');
      } catch (err) {}
    }

    const queryStartIndex = normalized.indexOf('?');
    if (queryStartIndex > -1) {
      normalized = normalized.slice(queryStartIndex + 1);
    }

    normalized = normalized.replace(/^[?&]+/, '');

    const hashIndex = normalized.indexOf('#');
    if (hashIndex > -1) {
      normalized = normalized.slice(0, hashIndex);
    }

    return normalized.trim();
  }

  function isInternalBogoParam(key) {
    return key === BOGO_RELOAD_MARKER;
  }

  function getBogoConfig(root) {
    return {
      enabled:
        !!root &&
        root.getAttribute('data-osedd-bogo-url-params-enabled') === 'true',
      params:
        root && root.getAttribute('data-osedd-bogo-url-params')
          ? root.getAttribute('data-osedd-bogo-url-params')
          : '',
      checkoutUrl:
        root && root.getAttribute('data-osedd-checkout-url')
          ? root.getAttribute('data-osedd-checkout-url')
          : '/checkout',
    };
  }

  function configuredBogoParamsMatchUrl(sanitizedParams) {
    const incoming = new URLSearchParams(sanitizedParams);
    const current = new URLSearchParams(window.location.search);
    let matches = true;

    incoming.forEach((value, key) => {
      if (!key) return;

      if (current.get(key) !== value) {
        matches = false;
      }
    });

    return matches;
  }

  function mergeBogoParamsIntoUrl(baseUrl, sanitizedParams) {
    const mergedUrl = new URL(baseUrl, window.location.origin);
    const incomingParams = new URLSearchParams(sanitizedParams);
    let hasValidParam = false;

    incomingParams.forEach((value, key) => {
      if (!key) return;

      mergedUrl.searchParams.set(key, value);
      hasValidParam = true;
    });

    return hasValidParam ? mergedUrl : null;
  }

  function stripInternalBogoParamsFromCurrentUrl() {
    const url = new URL(window.location.href);

    if (!url.searchParams.has(BOGO_RELOAD_MARKER)) return;

    url.searchParams.delete(BOGO_RELOAD_MARKER);

    const nextRelativeUrl =
      url.pathname + (url.search || '') + url.hash;

    window.history.replaceState({}, '', nextRelativeUrl);
  }

  function applyBogoUrlParams(root, options = {}) {
    const config = getBogoConfig(root);
    const allowReload = !!options.allowReload;

    if (!config.enabled) return false;

    const sanitizedParams = normalizeBogoParamString(config.params);
    if (!sanitizedParams) return false;

    if (configuredBogoParamsMatchUrl(sanitizedParams)) {
      stripInternalBogoParamsFromCurrentUrl();
      return false;
    }

    const mergedUrl = mergeBogoParamsIntoUrl(
      window.location.href,
      sanitizedParams
    );

    if (!mergedUrl) return false;

    if (allowReload) {
      const currentUrl = new URL(window.location.href);

      if (!currentUrl.searchParams.has(BOGO_RELOAD_MARKER)) {
        mergedUrl.searchParams.set(BOGO_RELOAD_MARKER, '1');
        window.location.replace(mergedUrl.toString());
        return true;
      }
    }

    mergedUrl.searchParams.delete(BOGO_RELOAD_MARKER);

    const nextRelativeUrl =
      mergedUrl.pathname +
      (mergedUrl.search || '') +
      mergedUrl.hash;

    window.history.replaceState({}, '', nextRelativeUrl);

    return false;
  }

  function extractBogoCartAttributes(root) {
    const config = getBogoConfig(root);

    if (!config.enabled) return null;

    const sanitizedParams = normalizeBogoParamString(config.params);
    if (!sanitizedParams) return null;

    const attributes = {};
    let hasAny = false;

    new URLSearchParams(sanitizedParams).forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;

      attributes[key] = value;
      hasAny = true;
    });

    return hasAny ? attributes : null;
  }

  function copyPublicSearchParams(sourceParams, targetUrl) {
    sourceParams.forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;

      targetUrl.searchParams.set(key, value);
    });
  }

  function mergeConfiguredBogoParams(checkoutUrl, root) {
    const config = getBogoConfig(root);

    if (!config.enabled) return;

    const sanitizedParams = normalizeBogoParamString(config.params);
    if (!sanitizedParams) return;

    new URLSearchParams(sanitizedParams).forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;

      checkoutUrl.searchParams.set(key, value);
    });
  }

  function getCheckoutUrlWithCurrentParams(root, discountCodes) {
    const config = getBogoConfig(root);
    const checkoutUrl = new URL(
      config.checkoutUrl || '/checkout',
      window.location.origin
    );

    copyPublicSearchParams(
      new URLSearchParams(window.location.search),
      checkoutUrl
    );

    mergeConfiguredBogoParams(checkoutUrl, root);

    if (discountCodes.length) {
      checkoutUrl.searchParams.set(
        'discount',
        discountCodes.join(',')
      );
    }

    if (checkoutUrl.origin === window.location.origin) {
      return (
        checkoutUrl.pathname +
        checkoutUrl.search +
        checkoutUrl.hash
      );
    }

    return checkoutUrl.toString();
  }

  function parseDiscountCodes(raw) {
    const text = (raw || '').trim();
    if (!text) return [];
    // Accept comma or newline separated
    return text
      .split(/[\n,]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  async function postJSON(url, bodyObj) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(bodyObj),
      credentials: 'same-origin',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  async function addItemsBatch(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items: items || [] }),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  async function addSingleItem(item) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(item || {}),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      const msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!res.ok) {
      throw new Error(`Unable to read cart (${res.status})`);
    }

    return res.json();
  }

  function waitForBogoGift(knownItemCount) {
    return new Promise((resolve) => {
      let elapsed = 0;
      const interval = 200;
      const maxWait = 3000;

      const checkCart = async () => {
        try {
          const cart = await fetchCart();

          if (
            cart &&
            typeof cart.item_count === 'number' &&
            cart.item_count > knownItemCount
          ) {
            resolve(true);
            return;
          }
        } catch (err) {
          resolve(false);
          return;
        }

        elapsed += interval;

        if (elapsed >= maxWait) {
          resolve(false);
          return;
        }

        window.setTimeout(checkCart, interval);
      };

      window.setTimeout(checkCart, interval);
    });
  }

  function wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function setStatus(root, msg) {
    const el = root.querySelector('[data-osedd-status]');
    if (!el) return;
    el.textContent = msg || '';
  }

  function getScrollOffsetPx() {
    // If your theme header is visible/sticky, account for it.
    // If header is hidden (like your CSS), this will end up 0.
    try {
      const header = document.querySelector('.header-wrapper');
      if (!header) return 0;

      const style = window.getComputedStyle(header);
      if (style.display === 'none' || style.visibility === 'hidden') return 0;

      // Only offset if it’s fixed/sticky (common for Shopify headers)
      const pos = (style.position || '').toLowerCase();
      if (pos !== 'fixed' && pos !== 'sticky') return 0;

      const h = Math.round(header.getBoundingClientRect().height || 0);
      return Math.max(0, h);
    } catch (e) {
      return 0;
    }
  }

  function smoothScrollToTarget(targetEl) {
    if (!targetEl) return;

    const offset = getScrollOffsetPx();
    const rect = targetEl.getBoundingClientRect();
    const y = Math.round(window.pageYOffset + rect.top - offset - 12); // 12px breathing room

    window.scrollTo({
      top: Math.max(0, y),
      behavior: 'smooth',
    });
  }

  function bindInfoDropdowns(root) {
    const items = root.querySelectorAll('.osedd-topshop__infoItem');
    if (!items || !items.length) return;

    items.forEach((item) => {
      const btn = item.querySelector('[data-osedd-info-btn]');
      const panel = item.querySelector('[data-osedd-info-panel]');
      if (!btn || !panel) return;

      btn.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        // Close all siblings (accordion behavior)
        items.forEach((sib) => {
          if (sib === item) return;
          sib.classList.remove('is-open');
          const sibBtn = sib.querySelector('[data-osedd-info-btn]');
          const sibPanel = sib.querySelector('[data-osedd-info-panel]');
          if (sibBtn) sibBtn.setAttribute('aria-expanded', 'false');
          if (sibPanel) sibPanel.hidden = true;
        });

        if (isOpen) {
          item.classList.remove('is-open');
          btn.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
        } else {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
        }
      });
    });
  }

  function placeCliniciansChoice(root) {
    if (!root) return;

    const badge = root.querySelector('[data-osedd-clinicians-choice]');
    const mobileMount = root.querySelector(
      '[data-osedd-clinicians-choice-mobile]'
    );
    const desktopMount = root.querySelector(
      '[data-osedd-clinicians-choice-desktop]'
    );

    if (!badge || !mobileMount || !desktopMount) return;

    const targetMount = window.matchMedia('(max-width: 899px)').matches
      ? mobileMount
      : desktopMount;

    if (badge.parentElement !== targetMount) {
      targetMount.appendChild(badge);
    }
  }

  function bindCliniciansChoicePlacement(root) {
    if (!root) return;

    placeCliniciansChoice(root);

    if (root.__oseddCliniciansChoicePlacementBound) return;
    root.__oseddCliniciansChoicePlacementBound = true;

    const mediaQuery = window.matchMedia('(max-width: 899px)');
    const handleBreakpointChange = () => {
      placeCliniciansChoice(root);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleBreakpointChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleBreakpointChange);
    }
  }

  function bindRteScrollLinks(root) {
    // Only handle anchor links inside this section’s RTE areas
    const rteContainers = root.querySelectorAll(
      '.osedd-topshop__offerBody.rte'
    );
    if (!rteContainers || !rteContainers.length) return;

    rteContainers.forEach((wrap) => {
      wrap.addEventListener('click', (e) => {
        const a = e.target && e.target.closest ? e.target.closest('a') : null;
        if (!a) return;

        const href = (a.getAttribute('href') || '').trim();
        if (!href || href === '#') return;

        // Only intercept same-page hash links (e.g. #reviews)
        if (href.charAt(0) !== '#') return;

        const id = href.slice(1);
        if (!id) return;

        const target = document.getElementById(id);
        if (!target) return;

        e.preventDefault();
        smoothScrollToTarget(target);

        // Optional: keep URL hash in sync without jumping
        try {
          history.pushState(null, '', `#${id}`);
        } catch (err) {}
      });
    });
  }

  function setStickyVisible(root, isVisible) {
    if (!root) return;

    const sticky = root.querySelector(SELECTOR_STICKY);
    if (!sticky) return;

    if (isVisible) {
      root.classList.add('osedd-sticky-active');
      sticky.setAttribute('aria-hidden', 'false');
    } else {
      root.classList.remove('osedd-sticky-active');
      sticky.setAttribute('aria-hidden', 'true');
    }
  }

  function bindStickyObserver(root, mainCta) {
    if (!root || !mainCta) return;

    // Avoid double-binding observers.
    if (root.__oseddStickyObserved) return;
    root.__oseddStickyObserved = true;

    const sticky = root.querySelector(SELECTOR_STICKY);
    if (!sticky) return;

    const flowersWrap = sticky.querySelector(
      '[data-osedd-sticky-flowers]'
    );

    if (flowersWrap && !flowersWrap.hasChildNodes()) {
      const iconStyle =
        flowersWrap.getAttribute('data-osedd-sticky-icon-style') || 'flowers';
      const customEmojis = (
        flowersWrap.getAttribute('data-osedd-sticky-emojis') || ''
      )
        .split(/[,\n]+/)
        .map((emoji) => emoji.trim())
        .filter(Boolean);
      const imageUrls = [1, 2, 3, 4]
        .map((slot) =>
          (flowersWrap.getAttribute(`data-osedd-sticky-image-${slot}`) || '').trim()
        )
        .filter(Boolean);
      const isMobileFlowers = window.matchMedia(
        '(max-width: 899px)'
      ).matches;
      const flowerCount = isMobileFlowers ? 16 : 28;
      const flowerColors = [
        'is-pink',
        'is-yellow',
        'is-purple',
        'is-blue',
        'is-mint',
        'is-orange',
      ];
      const hasCustomIcons =
        iconStyle === 'custom_emojis' ||
        iconStyle === 'custom_images' ||
        iconStyle === 'custom_mix';
      const hasAvailableIcons = iconStyle === 'custom_emojis'
        ? customEmojis.length > 0
        : iconStyle === 'custom_images'
          ? imageUrls.length > 0
          : customEmojis.length > 0 || imageUrls.length > 0;

      if (!hasCustomIcons || hasAvailableIcons) {
        for (
          let flowerIndex = 0;
          flowerIndex < flowerCount;
          flowerIndex += 1
        ) {
          const flower = document.createElement('span');
          const useLeftSide = flowerIndex % 2 === 0;
          const flowerLeft = useLeftSide
            ? Math.random() * 30
            : 70 + Math.random() * 30;

          let iconType = 'flower';

          if (iconStyle === 'pumpkins') {
            iconType = 'pumpkin';
          } else if (iconStyle === 'ghosts') {
            iconType = 'ghost';
          } else if (iconStyle === 'halloween') {
            iconType = flowerIndex % 4 < 2 ? 'pumpkin' : 'ghost';
          } else if (iconStyle === 'custom_emojis') {
            iconType = 'emoji';
          } else if (iconStyle === 'custom_images') {
            iconType = 'image';
          } else if (iconStyle === 'custom_mix') {
            iconType = imageUrls.length && (!customEmojis.length || flowerIndex % 2 !== 0)
              ? 'image'
              : 'emoji';
          }

          const flowerSize = iconType === 'flower'
            ? (isMobileFlowers ? 9 + Math.random() * 7 : 11 + Math.random() * 10)
            : (isMobileFlowers ? 16 + Math.random() * 8 : 18 + Math.random() * 10);
          const flowerDrift = -18 + Math.random() * 36;
          const flowerRotation = iconType === 'flower'
            ? (Math.random() > 0.5 ? 360 : -360)
            : -20 + Math.random() * 40;

          flower.className = iconType === 'flower'
            ? `osedd-topshop__stickyFlower ${flowerColors[flowerIndex % flowerColors.length]}`
            : `osedd-topshop__stickyFlower is-${iconType}`;

          if (iconType !== 'flower') {
            flower.style.background = 'none';
            flower.style.display = 'flex';
            flower.style.alignItems = 'center';
            flower.style.justifyContent = 'center';
            flower.style.fontFamily = '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
            flower.style.fontSize = `${flowerSize.toFixed(2)}px`;
            flower.style.lineHeight = '1';
            flower.style.whiteSpace = 'nowrap';
          }

          const contentIndex =
            iconStyle === 'custom_mix' && customEmojis.length && imageUrls.length
              ? Math.floor(flowerIndex / 2)
              : flowerIndex;

          if (iconType === 'pumpkin' || iconType === 'ghost') {
            flower.textContent = iconType === 'pumpkin' ? '🎃' : '👻';
          } else if (iconType === 'emoji') {
            flower.textContent = customEmojis[contentIndex % customEmojis.length];
          } else if (iconType === 'image') {
            const image = document.createElement('img');
            image.src = imageUrls[contentIndex % imageUrls.length];
            image.alt = '';
            image.decoding = 'async';
            image.style.display = 'block';
            image.style.width = '100%';
            image.style.height = '100%';
            image.style.objectFit = 'contain';
            flower.appendChild(image);
          }

          flower.style.left = `${flowerLeft.toFixed(2)}%`;

          flower.style.setProperty(
            '--osedd-sticky-flower-size',
            `${flowerSize.toFixed(2)}px`
          );

          flower.style.setProperty(
            '--osedd-sticky-flower-duration',
            `${(4.5 + Math.random() * 4).toFixed(2)}s`
          );

          flower.style.setProperty(
            '--osedd-sticky-flower-delay',
            `${(-Math.random() * 9).toFixed(2)}s`
          );

          flower.style.setProperty(
            '--osedd-sticky-flower-drift-x',
            `${flowerDrift.toFixed(2)}px`
          );

          flower.style.setProperty(
            '--osedd-sticky-flower-rotation',
            `${flowerRotation}deg`
          );

          flower.style.setProperty(
            '--osedd-sticky-flower-static-top',
            `${(12 + Math.random() * 76).toFixed(2)}%`
          );

          flower.style.setProperty(
            '--osedd-sticky-flower-static-rotation',
            `${(-30 + Math.random() * 60).toFixed(2)}deg`
          );

          flowersWrap.appendChild(flower);
        }
      }
    }

    const triggerMode =
      sticky.getAttribute('data-osedd-sticky-trigger') ||
      'main_cta_out_of_view';

    const updateSticky = () => {
      if (triggerMode === 'below_top_shop') {
        const sectionRect = root.getBoundingClientRect();
        const isBelowTopShop =
          sectionRect.bottom <= window.innerHeight;

        setStickyVisible(root, isBelowTopShop);
        return;
      }

      const ctaRect = mainCta.getBoundingClientRect();
      const inView =
        ctaRect.bottom > 0 &&
        ctaRect.top < window.innerHeight;

      setStickyVisible(root, !inView);
    };

    window.addEventListener('scroll', updateSticky, {
      passive: true,
    });
    window.addEventListener('resize', updateSticky);
    updateSticky();
  }

  function setBusy(btn, isBusy) {
    if (!btn) return;
    btn.disabled = !!isBusy;
    btn.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (isBusy) btn.classList.add('is-busy');
    else btn.classList.remove('is-busy');
  }

  async function handleCheckoutClick(btn) {
    const root = btn.closest(SELECTOR_ROOT);
    if (!root) return;

    if (root.__oseddCheckoutSubmitting) return;
    root.__oseddCheckoutSubmitting = true;

    const mainCta = root.querySelector(SELECTOR_CTA);
    const stickyCta = root.querySelector(SELECTOR_STICKY_CTA);

    setStatus(root, '');
    setBusy(mainCta, true);
    setBusy(stickyCta, true);

    try {
      applyBogoUrlParams(root);

      const clearCart = btn.getAttribute('data-osedd-clear-cart') === '1';
      const mainVariantIdRaw = (btn.getAttribute('data-osedd-main-variant') || '').trim();
      const mainQty = Number(btn.getAttribute('data-osedd-main-qty') || 1);
      const mainSellingPlanRaw = (btn.getAttribute('data-osedd-main-selling-plan') || '').trim();

      const mainVariantId = Number(mainVariantIdRaw || 0);
      const mainSellingPlanId = Number(mainSellingPlanRaw || 0);

      if (!mainVariantId) {
        throw new Error('Missing main variant ID. Please set it in the section settings.');
      }

      let freebies = [];
      const freebiesRaw = btn.getAttribute('data-osedd-freebies') || '[]';

      try {
        freebies = JSON.parse(freebiesRaw) || [];
      } catch (e) {
        freebies = [];
      }

      const mainItem = {
        id: mainVariantId,
        quantity: Math.max(1, mainQty || 1),
      };

      if (mainSellingPlanRaw && mainSellingPlanId > 0) {
        mainItem.selling_plan = mainSellingPlanId;
      }

      const freebieItems = (freebies || [])
        .filter((it) => it && Number(it.id) > 0)
        .map((it) => ({
          id: Number(it.id),
          quantity: Math.max(1, Number(it.quantity) || 1),
        }));

      const discountCodes = parseDiscountCodes(
        btn.getAttribute('data-osedd-discounts')
      );
      const bogoAttributes = extractBogoCartAttributes(root);

      if (clearCart) {
        await postJSON('/cart/clear.js', {});
        await wait(180);
      }

      let cartBeforeAdd = null;

      try {
        cartBeforeAdd = await fetchCart();
      } catch (err) {
        cartBeforeAdd = null;
      }

      await addSingleItem(mainItem);

      let addedQuantity = Math.max(1, Number(mainItem.quantity) || 1);

      if (freebieItems.length) {
        try {
          await addItemsBatch(freebieItems);

          addedQuantity += freebieItems.reduce(
            (total, item) =>
              total + Math.max(1, Number(item.quantity) || 1),
            0
          );
        } catch (freebieErr) {
          console.warn('OS EDD freebies failed to add:', freebieErr);
        }
      }

      let cartAfterAdd = null;

      try {
        cartAfterAdd = await fetchCart();
      } catch (err) {
        cartAfterAdd = null;
      }

      let cartAfterAttributeUpdate = null;

      if (bogoAttributes) {
        cartAfterAttributeUpdate = await postJSON('/cart/update.js', {
          attributes: bogoAttributes,
        });
      }

      if (bogoAttributes) {
        const latestCart =
          cartAfterAttributeUpdate || cartAfterAdd || null;
        const knownItemCount =
          latestCart && typeof latestCart.item_count === 'number'
            ? latestCart.item_count
            : null;
        const baselineItemCount =
          cartBeforeAdd && typeof cartBeforeAdd.item_count === 'number'
            ? cartBeforeAdd.item_count
            : null;
        const giftAlreadyPresent =
          knownItemCount !== null &&
          baselineItemCount !== null &&
          knownItemCount > baselineItemCount + addedQuantity;

        if (!giftAlreadyPresent && knownItemCount !== null) {
          await waitForBogoGift(knownItemCount);
        }
      }

      applyBogoUrlParams(root);

      window.location.href = getCheckoutUrlWithCurrentParams(
        root,
        discountCodes
      );
      return;
    } catch (err) {
      const msg =
        err && err.message
          ? err.message
          : 'Unable to start checkout. Please try again.';

      setStatus(root, msg);
      setBusy(mainCta, false);
      setBusy(stickyCta, false);
      root.__oseddCheckoutSubmitting = false;
    }
  }

  function getSyncRole(root) {
    return root
      ? (root.getAttribute('data-osedd-sync-role') || 'standalone').trim()
      : 'standalone';
  }

  function getSyncGroup(root) {
    return root
      ? (root.getAttribute('data-osedd-sync-group') || '').trim()
      : '';
  }

  function rewriteSyncedIds(root, sourceSectionId, targetSectionId) {
    if (
      !root ||
      !sourceSectionId ||
      !targetSectionId ||
      sourceSectionId === targetSectionId
    ) {
      return;
    }

    const idMap = new Map();

    root.querySelectorAll('[id]').forEach((element) => {
      const oldId = element.id;
      if (!oldId || !oldId.includes(sourceSectionId)) return;

      const newId = oldId.split(sourceSectionId).join(targetSectionId);
      element.id = newId;
      idMap.set(oldId, newId);
    });

    if (!idMap.size) return;

    const idReferenceAttributes = [
      'aria-controls',
      'aria-labelledby',
      'aria-describedby',
      'for',
    ];

    root.querySelectorAll('*').forEach((element) => {
      idReferenceAttributes.forEach((attributeName) => {
        const rawValue = element.getAttribute(attributeName);
        if (!rawValue) return;

        const nextValue = rawValue
          .split(/\s+/)
          .map((value) => idMap.get(value) || value)
          .join(' ');

        if (nextValue !== rawValue) {
          element.setAttribute(attributeName, nextValue);
        }
      });

      const href = element.getAttribute('href');
      if (!href || href.charAt(0) !== '#') return;

      const referencedId = href.slice(1);
      if (idMap.has(referencedId)) {
        element.setAttribute('href', `#${idMap.get(referencedId)}`);
      }
    });
  }

  function removeCopiedEditorBlockAttributes(root) {
    if (!root) return;

    root.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
      element.removeAttribute('data-shopify-editor-block');
    });
  }

  function revealSyncCopy(root) {
    if (!root) return;

    root.style.removeProperty('visibility');
    root.setAttribute('data-osedd-sync-ready', 'true');
  }

  function copyRootAttributes(source, target) {
    const targetScrollId = target.getAttribute('id') || '';
    const preservedAttributes = new Map([
      [
        'data-osedd-section-id',
        target.getAttribute('data-osedd-section-id') || '',
      ],
      [
        'data-osedd-sync-role',
        target.getAttribute('data-osedd-sync-role') || 'copy',
      ],
      [
        'data-osedd-sync-group',
        target.getAttribute('data-osedd-sync-group') || '',
      ],
      [
        'data-osedd-sync-ready',
        target.getAttribute('data-osedd-sync-ready') || 'false',
      ],
    ]);

    Array.from(target.attributes).forEach((attribute) => {
      target.removeAttribute(attribute.name);
    });

    Array.from(source.attributes).forEach((attribute) => {
      target.setAttribute(attribute.name, attribute.value);
    });

    preservedAttributes.forEach((value, name) => {
      target.setAttribute(name, value);
    });

    if (targetScrollId) {
      target.setAttribute('id', targetScrollId);
    } else {
      target.removeAttribute('id');
    }
  }

  function syncRootFromSource(source, target) {
    if (!source || !target || source === target) return;

    const sourceSectionId =
      source.getAttribute('data-osedd-section-id') || '';
    const targetSectionId =
      target.getAttribute('data-osedd-section-id') || '';

    copyRootAttributes(source, target);
    target.classList.remove('osedd-sticky-active');
    target.innerHTML = source.innerHTML;

    rewriteSyncedIds(target, sourceSectionId, targetSectionId);
    removeCopiedEditorBlockAttributes(target);

    // Keep only one fixed sticky CTA on the page. The Master owns it.
    const copiedSticky = target.querySelector(SELECTOR_STICKY);
    if (copiedSticky) copiedSticky.remove();

    target.__oseddBound = false;
    target.__oseddStickyObserved = false;
    target.__oseddCheckoutSubmitting = false;

    revealSyncCopy(target);
  }

  function syncBuyBoxCopies() {
    const roots = Array.from(document.querySelectorAll(SELECTOR_ROOT));
    const sourcesByGroup = new Map();

    roots.forEach((root) => {
      if (getSyncRole(root) !== 'source') return;

      const group = getSyncGroup(root);
      if (!group) return;

      if (sourcesByGroup.has(group)) {
        console.warn(
          `OS EDD found more than one Master in sync group "${group}". The first Master on the page will be used.`
        );
        return;
      }

      sourcesByGroup.set(group, root);
    });

    roots.forEach((root) => {
      if (getSyncRole(root) !== 'copy') return;

      const group = getSyncGroup(root);
      const source = group ? sourcesByGroup.get(group) : null;

      if (!source) {
        revealSyncCopy(root);

        if (!root.__oseddMissingSyncSourceWarned) {
          const missingGroupName = group || '(blank)';

          console.warn(
            `OS EDD Copy could not find a Master in sync group "${missingGroupName}". Its own section settings will be used instead.`
          );

          root.__oseddMissingSyncSourceWarned = true;
        }

        return;
      }

      syncRootFromSource(source, root);
    });
  }

  function bindRoot(root) {
    if (!root || root.__oseddBound) return;

    if (applyBogoUrlParams(root, { allowReload: true })) {
      return;
    }

    root.__oseddBound = true;

    const cta = root.querySelector(SELECTOR_CTA);
    if (cta) {
      cta.addEventListener('click', () => handleCheckoutClick(cta));
    }

    const stickyCta = root.querySelector(SELECTOR_STICKY_CTA);
    if (stickyCta) {
      stickyCta.addEventListener('click', () => {
        handleCheckoutClick(stickyCta);
      });
    }

    // Only a Master or Independent instance can own a fixed sticky CTA.
    if (cta && root.querySelector(SELECTOR_STICKY)) {
      bindStickyObserver(root, cta);
    }

    // Keep one live Clinicians' Choice iframe and move it by breakpoint.
    bindCliniciansChoicePlacement(root);

    // Dropdown info accordions (desktop + mobile placements)
    bindInfoDropdowns(root);

    // Smooth-scroll for hash links inside offer richtext.
    bindRteScrollLinks(root);

    // Gallery: image/video switching + thumb strip with side arrows
    const gallery = root.querySelector('[data-osedd-gallery]');
    const mainSlides = root.querySelectorAll(
      '[data-osedd-gal-main-slide]'
    );
    const thumbs = root.querySelectorAll('[data-osedd-thumb]');
    const thumbViewport = root.querySelector(
      '[data-osedd-thumb-viewport]'
    );
    const mainPrev = root.querySelector(
      '[data-osedd-main-nav="prev"]'
    );
    const mainNext = root.querySelector(
      '[data-osedd-main-nav="next"]'
    );

    // If thumbs exist, CSS moves the main arrows beside the thumbnail row.
    if (root.querySelector('.osedd-topshop__galThumbs')) {
      root.classList.add('osedd-has-thumbs');
    } else {
      root.classList.remove('osedd-has-thumbs');
    }

    if (gallery && mainSlides.length) {
      let index = 0;

      const syncMainNav = () => {
        if (!mainPrev || !mainNext) return;

        const max = Math.max(0, mainSlides.length - 1);
        mainPrev.disabled = index <= 0;
        mainNext.disabled = index >= max;
      };

      const setActive = (nextIndex) => {
        const max = Math.max(0, mainSlides.length - 1);
        index = Math.min(Math.max(nextIndex, 0), max);

        mainSlides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === index;
          const slideVideos = slide.querySelectorAll('video');

          slide.setAttribute(
            'aria-hidden',
            isActive ? 'false' : 'true'
          );

          if (isActive) {
            slide.setAttribute('data-osedd-active', '1');
          } else {
            slide.removeAttribute('data-osedd-active');
          }

          slideVideos.forEach((video) => {
            if (!isActive) {
              video.pause();
              return;
            }

            video.removeAttribute('muted');
            video.muted = false;
            video.defaultMuted = false;
            video.volume = 1;

            const playPromise = video.play();

            if (
              playPromise &&
              typeof playPromise.catch === 'function'
            ) {
              playPromise.catch(() => {});
            }
          });
        });

        thumbs.forEach((thumb, thumbIndex) => {
          const isActive = thumbIndex === index;

          thumb.setAttribute(
            'aria-selected',
            isActive ? 'true' : 'false'
          );

          if (isActive) {
            thumb.setAttribute('data-osedd-active', '1');
          } else {
            thumb.removeAttribute('data-osedd-active');
          }
        });

        // Keep the active thumbnail fully visible without moving the page.
        const activeThumb = thumbs[index];

        if (activeThumb && thumbViewport) {
          const viewportRect = thumbViewport.getBoundingClientRect();
          const thumbRect = activeThumb.getBoundingClientRect();
          let nextScrollLeft = thumbViewport.scrollLeft;

          if (thumbRect.left < viewportRect.left) {
            nextScrollLeft -= viewportRect.left - thumbRect.left;
          } else if (thumbRect.right > viewportRect.right) {
            nextScrollLeft += thumbRect.right - viewportRect.right;
          }

          if (nextScrollLeft !== thumbViewport.scrollLeft) {
            thumbViewport.scrollTo({
              left: Math.max(0, nextScrollLeft),
              behavior: 'smooth',
            });
          }
        }

        syncMainNav();
      };

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const thumbIndex = Number(
            thumb.getAttribute('data-osedd-gal-index') || 0
          );

          setActive(thumbIndex);
        });
      });

      if (mainPrev) {
        mainPrev.addEventListener('click', (event) => {
          if (
            event &&
            typeof event.preventDefault === 'function'
          ) {
            event.preventDefault();
          }

          setActive(index - 1);
        });
      }

      if (mainNext) {
        mainNext.addEventListener('click', (event) => {
          if (
            event &&
            typeof event.preventDefault === 'function'
          ) {
            event.preventDefault();
          }

          setActive(index + 1);
        });
      }

      if (thumbViewport) {
        thumbViewport.style.scrollBehavior = 'smooth';
      }

      setActive(0);
    }
  }

  function init() {
    syncBuyBoxCopies();
    document.querySelectorAll(SELECTOR_ROOT).forEach(bindRoot);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);

  if (document.readyState !== 'loading') {
    init();
  }
})();